import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { ADMIN_PAGES_KEYS, NS, ORG_SETTINGS_KEYS } from '../../../../i18n/constants/keys';
import {
  BadgeCheckIcons,
  LayersIcons,
  UserSquareIcons,
} from '../../../../icons/icons';
import { FrameworkStatsHeaderProps } from '../interfaces';

export const FrameworkStatsHeader = ({
  skillsCount,
  subSkillsCount,
  jobTitlesCount,
}: FrameworkStatsHeaderProps) => {
  const { t } = useTranslation(NS.adminPages);

  return (
    <>
      <Box sx={{ width: '100%', borderBottom: '1px solid', borderColor: 'divider', py: 1.5 }}>
        <Breadcrumbs aria-label='breadcrumb' separator='/'>
          <Link
            component={RouterLink}
            to='#'
            underline='hover'
            sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
          >
            {t(ORG_SETTINGS_KEYS.breadcrumbs.admin)}
          </Link>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
            {t(ADMIN_PAGES_KEYS.assessmentFramework.title)}
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
        <Paper
          variant='outlined'
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'background.paper',
            borderColor: 'divider',
            borderRadius: '6px',
            boxShadow: 'none',
            overflow: 'hidden',
          }}
        >
          <Stack direction='row' alignItems='center' spacing={1} sx={{ px: 2.5, py: 1 }}>
            <Box sx={{ color: '#64748b', display: 'flex', alignItems: 'center', '& svg': { width: 16, height: 16 } }}>
              <LayersIcons />
            </Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', fontFamily: 'monospace' }}>
              {skillsCount.toLocaleString()}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {t(ADMIN_PAGES_KEYS.assessmentFramework.skills)}
            </Typography>
          </Stack>

          <Divider orientation='vertical' flexItem sx={{ my: 0.5 }} />

          <Stack direction='row' alignItems='center' spacing={1} sx={{ px: 2.5, py: 1 }}>
            <Box sx={{ color: '#64748b', display: 'flex', alignItems: 'center', '& svg': { width: 16, height: 16 } }}>
              <BadgeCheckIcons />
            </Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', fontFamily: 'monospace' }}>
              {subSkillsCount.toLocaleString()}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {t(ADMIN_PAGES_KEYS.assessmentFramework.subSkills)}
            </Typography>
          </Stack>

          <Divider orientation='vertical' flexItem sx={{ my: 0.5 }} />

          <Stack direction='row' alignItems='center' spacing={1} sx={{ px: 2.5, py: 1 }}>
            <Box sx={{ color: '#64748b', display: 'flex', alignItems: 'center', '& svg': { width: 16, height: 16 } }}>
              <UserSquareIcons />
            </Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', fontFamily: 'monospace' }}>
              {jobTitlesCount.toLocaleString()}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {t(ADMIN_PAGES_KEYS.assessmentFramework.jobTitles)}
            </Typography>
          </Stack>
        </Paper>
      </Box>
    </>
  );
};
